import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import API_BASE from "../apiBase";
import {
  Activity,
  Bell,
  FileCheck,
  FileSpreadsheet,
  Lightbulb,
  LayoutDashboard,
  LogOut,
  BookOpen,
  Headphones,
  Users,
  Server,
  AlertTriangle,
  Brain,
  Building2,
  MapPin,
  AlertCircle,
  Briefcase,
  LifeBuoy,
  GitCompareArrows,
  ScanLine,
  Newspaper,
} from "lucide-react";

const ALL_MODULES = [
  {
    moduleKey: "daily_report",
    title: "DAILY REPORT",
    status: "LIVE",
    icon: Activity,
    route: null,
    crossDomainTarget: "report",
    description:
      "Real-time AED system status across all subscribers. AI-analyzed device health, trends and status breakdowns.",
    badgeCount: null,
  },
  {
    moduleKey: "notifications",
    title: "NOTIFICATIONS",
    status: "IN DEV",
    icon: Bell,
    route: null,
    crossDomainTarget: "notifications",
    description:
      "Subscriber issue alerts and email dispatch console. Track sent notifications and actionable AED events.",
    badgeCount: 16,
  },
  {
    moduleKey: "service_tickets",
    title: "SERVICE TICKETS",
    status: "ACTIVE",
    icon: FileCheck,
    route: "/service-tickets",
    externalUrl: null,
    description:
      "Field technician dispatch, ticket lifecycle management, and AED service workflow from open to confirmed.",
    badgeCount: null,
  },
  {
    moduleKey: "support_dashboard",
    title: "SUPPORT DASHBOARD",
    status: "LIVE",
    icon: LifeBuoy,
    route: "/support-dashboard",
    description:
      "Subscriber support workspace. Notify subscribers, track readiness, manage detail templates, and review email engagement.",
    badgeCount: null,
  },
  {
    moduleKey: "dashboard",
    title: "DASHBOARD",
    status: "IN DEV",
    icon: LayoutDashboard,
    route: "/dashboard",
    description:
      "Comprehensive AED monitoring dashboard with system status, service summary, AI recommendations, and voice query.",
    badgeCount: null,
  },
  {
    moduleKey: "map",
    title: "MAP",
    status: "LIVE",
    icon: MapPin,
    route: "/map",
    description:
      "Interactive USA map displaying subscriber AED locations, device clusters, and regional coverage overview.",
    badgeCount: null,
  },
  {
    moduleKey: "survival_path",
    title: "SURVIVAL PATH",
    status: "IN DEV",
    icon: Lightbulb,
    route: null,
    description:
      "Cardiac event outcome tracking, response analytics, and chain-of-survival documentation per incident.",
    badgeCount: null,
  },
  {
    moduleKey: "user_access",
    title: "USER ACCESS",
    status: "IN DEV",
    icon: Users,
    route: "/user-access",
    description:
      "Add, edit, and remove authorized users. Control which modules each operator can access from their hub.",
    badgeCount: null,
  },
  {
    moduleKey: "backend",
    title: "BACKEND",
    status: "IN DEV",
    icon: Server,
    route: "/backend",
    description:
      "Monitor and control backend processes, system health, database status, and server performance metrics.",
    badgeCount: null,
  },
  {
    moduleKey: "sales",
    title: "SALES",
    status: "LIVE",
    icon: Briefcase,
    route: "/sales",
    description:
      "Upload sales route plans (.xlsx or .csv), assign them to salesmen, view stops on a map, and track route completion. Supports per-day stops with starting/ending cities, regions, and counties.",
    badgeCount: null,
  },
  {
    moduleKey: "sales_field",
    title: "FIELD PORTAL",
    status: "LIVE",
    icon: Briefcase,
    route: "/sales/mobile",
    description:
      "Mobile field view for sales reps: view assigned routes, get directions, log visits with GPS + notes, and capture per-stop CRM recaps (contact, interest level, follow-up, action).",
    badgeCount: null,
  },
  {
    moduleKey: "user_activity",
    title: "USER ACTIVITY",
    status: "LIVE",
    icon: Activity,
    route: "/admin/user-activity",
    adminOnly: true,
    futureroadsOnly: true,
    description:
      "Track who is currently using the system (live), what page they're on, and a 90-day history of every login session, page visited, and AEDA voice usage. CSV export available.",
    badgeCount: null,
  },
  {
    moduleKey: "reports",
    title: "REPORTS",
    status: "LIVE",
    icon: FileSpreadsheet,
    route: "/admin/reports",
    description:
      "Fleet reporting. Run a By-Model report — every AED of a chosen model (or ALL models filtered by subscriber), its status, and battery / pads expiration. Export to Excel.",
    badgeCount: null,
  },
  {
    moduleKey: "location_contacts",
    title: "LOCATION CONTACTS",
    status: "LIVE",
    icon: Building2,
    route: "/admin/location-contacts",
    description:
      "Manage per-location notification recipients for subscribers that send by location (e.g. Georgia Power). Import an XLSX or edit inline.",
    badgeCount: null,
  },
  {
    moduleKey: "expired_bp_automation",
    title: "EXPIRED B/P NOTIFICATIONS",
    status: "LIVE",
    icon: AlertTriangle,
    route: "/admin/expired-bp-automation",
    description:
      "Automated daily emails to subscribers when AEDs cross into expired-battery/pads, plus 90/60/30-day EXPIRING B/P heads-ups. Upload templates, toggle per subscriber, and set send times.",
    badgeCount: null,
  },
  {
    moduleKey: "status_comparison",
    title: "STATUS COMPARISON REPORT",
    status: "LIVE",
    icon: GitCompareArrows,
    route: "/reports/status-comparison",
    description:
      "Side-by-side compare Readisys detailed status vs. the AI-assigned status for every AED in a subscriber's fleet. Download the report as XLSX with images embedded.",
    badgeCount: null,
  },
  {
    moduleKey: "scan2link",
    title: "SCAN2LINK",
    status: "LIVE",
    icon: ScanLine,
    route: "/scan2link",
    description:
      "Field-tech tool: scan a tracker's QR (IMEI), then scan the AED barcode, and link the two. Auto-looks up model + location in Readisys. Georgia Power gets an optional location picker.",
    badgeCount: null,
  },
  {
    moduleKey: "industry_overwatch",
    title: "INDUSTRY OVERWATCH",
    status: "LIVE",
    icon: Newspaper,
    route: "/admin/industry-overwatch",
    description:
      "Daily AED-industry news digest. GPT-5.5 searches the web for new products, FDA recalls, cardiac arrest research and more, then emails a summarized briefing to your curated recipient list.",
    badgeCount: null,
  },
  {
    moduleKey: "email_log",
    title: "EMAIL LOG",
    status: "LIVE",
    icon: AlertCircle,
    route: "/admin/email-errors",
    description:
      "Admin-only investigation tool. Inspect every notification email sent (success or failure), filter by user, and drill into errors when staff report send issues.",
    badgeCount: null,
  },
  {
    moduleKey: "outage_status",
    title: "OUTAGE STATUS",
    status: "IN DEV",
    icon: AlertTriangle,
    route: "/outage",
    description:
      "Monitor the real time status of major cloud services, cellular carriers and more.",
    badgeCount: null,
  },
  {
    moduleKey: "hybrid_training",
    title: "HYBRID FEEDBACK TRAINING",
    status: "IN DEV",
    icon: Brain,
    route: "/hybridtraining",
    description:
      "Monitor and manage OpenCV + Qwen hybrid AI training pipeline. Review user feedback, analyze misclassifications, and apply model updates.",
    badgeCount: null,
  },
  {
    moduleKey: "customer_portal",
    title: "CUSTOMER PORTAL",
    status: "IN DEV",
    icon: Building2,
    route: "/customerportal",
    description:
      "Add and manage customer site information, contact details, and AED unit inventory with serial numbers, locations, and service dates.",
    badgeCount: null,
  },
];

function ModuleCard({ module, index, onNavigate, authToken }) {
  const [loading, setLoading] = useState(false);
  const Icon = module.icon;
  const hasRoute = !!module.route || !!module.externalUrl || !!module.crossDomainTarget;
  const statusColor =
    module.status === "LIVE" ? "#22c55e" : module.status === "ADMIN" ? "#ef4444" : "#eab308";
  const statusBg =
    module.status === "LIVE"
      ? "rgba(34, 197, 94, 0.1)"
      : module.status === "ADMIN"
        ? "rgba(239, 68, 68, 0.1)"
        : "rgba(234, 179, 8, 0.1)";
  const statusBorder =
    module.status === "LIVE"
      ? "rgba(34, 197, 94, 0.3)"
      : module.status === "ADMIN"
        ? "rgba(239, 68, 68, 0.3)"
        : "rgba(234, 179, 8, 0.3)";

  const handleClick = async () => {
    if (module.crossDomainTarget) {
      setLoading(true);
      // Wake server first
      try { await fetch(`${API_BASE}/api/health`); } catch {}
      // SSO flow with retry: get signed token, then redirect
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
          const res = await fetch(`${API_BASE}/api/auth/cross-domain-token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ target: module.crossDomainTarget }),
          });
          if ([502, 503, 520, 521].includes(res.status) && attempt < 3) {
            continue;
          }
          if (!res.ok) throw new Error(`Server returned ${res.status}`);
          const data = await res.json();
          const win = window.open('', '_blank');
          if (win) {
            win.document.write('<!DOCTYPE html><html style="background:#020617"><head><title>Redirecting...</title></head><body style="background:#020617;color:#06b6d4;font-family:monospace;display:flex;align-items:center;justify-content:center;height:100vh;margin:0"><div style="text-align:center"><div style="width:40px;height:40px;border:3px solid rgba(6,182,212,0.2);border-top-color:#06b6d4;border-radius:50%;animation:s .8s linear infinite;margin:0 auto"></div><style>@keyframes s{to{transform:rotate(360deg)}}</style><p style="margin-top:16px;font-size:11px;letter-spacing:3px;opacity:0.7">ESTABLISHING SECURE CONNECTION...</p></div></body></html>');
            win.document.close();
            win.location.href = data.redirect_url;
          } else {
            window.location.href = data.redirect_url;
          }
          break;
        } catch (err) {
          if (attempt >= 3) {
            console.error("Cross-domain SSO error:", err);
            alert("Could not open " + module.title + " — server may be waking up. Please try again.");
          }
        }
      }
      setLoading(false);
    } else if (module.externalUrl) {
      window.open(module.externalUrl, "_blank", "noopener,noreferrer");
    } else if (module.route) {
      onNavigate(module.route);
    }
  };

  return (
    <motion.div
      data-testid={`module-card-${module.moduleKey}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: hasRoute ? 1 : 0.6, y: 0 }}
      transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
      onClick={handleClick}
      className="relative group"
      style={{
        cursor: hasRoute ? "pointer" : "not-allowed",
      }}
    >
      <div
        className="relative overflow-hidden rounded-sm border transition-all duration-300"
        style={{
          background: "rgba(10, 15, 28, 0.85)",
          borderColor: hasRoute
            ? "rgba(6, 182, 212, 0.15)"
            : "rgba(148, 163, 184, 0.1)",
          minHeight: "260px",
        }}
      >
        {/* Grid overlay on card */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148,163,184,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.03) 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />

        {/* Hover glow */}
        {hasRoute && (
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(6, 182, 212, 0.06) 0%, transparent 70%)",
            }}
          />
        )}

        <div className="relative p-6 flex flex-col h-full">
          {/* Status badge */}
          <div className="flex justify-between items-start mb-6">
            <span
              className="font-tech text-[10px] tracking-[0.2em] px-2.5 py-0.5 rounded-sm"
              style={{
                color: statusColor,
                background: statusBg,
                border: `1px solid ${statusBorder}`,
              }}
            >
              {module.status}
            </span>
          </div>

          {/* Icon */}
          <div className="mb-5 relative">
            <Icon
              size={38}
              strokeWidth={1.4}
              style={{ color: "#06b6d4" }}
            />
            {module.badgeCount && (
              <span
                className="absolute -top-1.5 -right-1 font-tech text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full"
                style={{
                  background: "#ef4444",
                  color: "#fff",
                  left: "32px",
                  right: "auto",
                }}
              >
                {module.badgeCount}
              </span>
            )}
          </div>

          {/* Module number */}
          <p
            className="font-tech text-[11px] tracking-[0.15em] mb-1.5"
            style={{ color: "#06b6d4" }}
          >
            MODULE #{index + 1}
          </p>

          {/* Title */}
          <h3
            className="font-tech text-lg tracking-wide mb-3"
            style={{ color: "#f1f5f9", fontWeight: 600 }}
          >
            {module.title}
          </h3>

          {/* Description */}
          <p
            className="text-[13px] leading-relaxed"
            style={{ color: "#94a3b8" }}
          >
            {module.description}
          </p>
        </div>

        {/* Bottom hover bar */}
        {hasRoute && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background:
                "linear-gradient(90deg, transparent, #06b6d4, transparent)",
            }}
          />
        )}
      </div>
    </motion.div>
  );
}

export default function CommandCenterHub({ user, onLogout }) {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [powering, setPowering] = useState(true);

  useEffect(() => {
    const id = setTimeout(() => setPowering(false), 1800);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const operatorName = user?.username?.toUpperCase() || "ADMIN";
  const userModules = user?.allowed_modules || [];
  const authToken = localStorage.getItem("token") || "";

  // Filter modules: show those the user has access to + admin modules for admin users.
  // futureroads-only modules are restricted strictly to that user.
  const visibleModules = ALL_MODULES.filter((m) => {
    if (m.futureroadsOnly) return user?.username === "futureroads";
    return userModules.includes(m.moduleKey) || user?.role === "admin";
  });

  if (powering) {
    return (
      <div
        data-testid="hub-powering-up"
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: "#000" }}
      >
        {/* Thin red line above text */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: 60 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-[1px] mb-4"
          style={{ background: "#ef4444" }}
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="font-tech text-sm tracking-[0.25em]"
          style={{ color: "#06b6d4" }}
        >
          COMMAND CENTER HUB POWERING UP
        </motion.p>
        {/* Subtle pulsing dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1, repeat: 1, delay: 0.4 }}
          className="flex gap-1.5 mt-3"
        >
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="w-1 h-1 rounded-full"
              style={{ background: "#ef4444" }}
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
            />
          ))}
        </motion.div>
      </div>
    );
  }

  return (
    <div
      data-testid="command-center-hub"
      className="min-h-screen flex flex-col"
      style={{ background: "#020617" }}
    >
      {/* Grid background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.04) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* HEADER */}
      <header
        data-testid="hub-header"
        className="relative z-10 flex items-center justify-between px-8 py-5 border-b"
        style={{ borderColor: "rgba(148, 163, 184, 0.08)" }}
      >
        {/* Left - Branding */}
        <div className="flex items-center gap-3">
          {/* Pulsing heart icon */}
          <div className="flex-shrink-0 heartbeat">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="#ef4444"
            >
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          </div>
          <div>
            <h1
              className="font-tech text-base tracking-[0.1em] leading-none"
              style={{ color: "#f1f5f9", fontWeight: 700 }}
            >
              CARDIAC SOLUTIONS
            </h1>
            <p
              className="font-tech text-[10px] tracking-[0.15em] mt-0.5"
              style={{ color: "#06b6d4" }}
            >
              AED MONITORING COMMAND CENTER
            </p>
          </div>
        </div>

        {/* Right - Status & Operator */}
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full"
              style={{
                background: "#22c55e",
                boxShadow: "0 0 6px rgba(34,197,94,0.6)",
              }}
            />
            <span
              className="font-tech text-[11px] tracking-[0.12em]"
              style={{ color: "#06b6d4" }}
              data-testid="system-status"
            >
              SYSTEM ONLINE
            </span>
          </div>
          <div
            className="px-3 py-1 rounded-sm border font-tech text-[11px] tracking-[0.1em]"
            style={{
              borderColor: "rgba(148, 163, 184, 0.2)",
              color: "#e2e8f0",
            }}
            data-testid="operator-badge"
          >
            OPERATOR: {operatorName}
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 px-3 py-1 rounded-sm border font-tech text-[11px] tracking-[0.1em] hover:bg-red-500/10 transition-colors"
            style={{
              borderColor: "rgba(239, 68, 68, 0.3)",
              color: "#ef4444",
            }}
            data-testid="header-logout"
          >
            <LogOut size={12} />
            LOGOUT
          </button>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 py-10">
        {/* Title block */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* COMMAND CENTER HUB */}
          <h2
            className="font-tech text-4xl sm:text-5xl tracking-[0.08em] mb-3"
            style={{ fontWeight: 700, lineHeight: 1.1 }}
          >
            <span style={{ color: "#f1f5f9" }}>COMMAND </span>
            <span style={{ color: "#ef4444" }}>CENTER </span>
            <span style={{ color: "#f1f5f9" }}>HUB</span>
          </h2>

          {/* Subtitle */}
          <p
            className="font-tech text-[11px] tracking-[0.18em]"
            style={{ color: "#06b6d4" }}
          >
            CARDIAC SOLUTIONS, LLC — INTEGRATED AED OPERATIONS PLATFORM
          </p>
        </motion.div>

        {/* Module cards grid */}
        <div
          className="w-full max-w-[1200px] grid gap-5"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          }}
        >
          {visibleModules.map((mod, i) => (
            <ModuleCard
              key={mod.moduleKey}
              module={mod}
              index={i}
              onNavigate={navigate}
              authToken={authToken}
            />
          ))}
        </div>
      </main>

      {/* FOOTER */}
      <footer
        data-testid="hub-footer"
        className="relative z-10 flex items-center justify-between px-8 py-4 border-t"
        style={{ borderColor: "rgba(148, 163, 184, 0.08)" }}
      >
        <div className="flex items-center gap-4">
          <span
            className="font-tech text-[10px] tracking-[0.1em]"
            style={{ color: "#64748b" }}
          >
            &copy; 2026 CARDIAC SOLUTIONS, LLC
          </span>
          <span
            className="font-tech text-[10px] tracking-[0.1em]"
            style={{ color: "#475569" }}
          >
            V2.1.0
          </span>
        </div>
        <div className="flex items-center gap-6">
          <button
            className="font-tech text-[10px] tracking-[0.12em] flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            style={{ color: "#94a3b8" }}
            data-testid="footer-documentation"
          >
            <BookOpen size={12} />
            DOCUMENTATION
          </button>
          <button
            className="font-tech text-[10px] tracking-[0.12em] flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            style={{ color: "#94a3b8" }}
            data-testid="footer-support"
          >
            <Headphones size={12} />
            SUPPORT
          </button>
          <button
            onClick={onLogout}
            className="font-tech text-[10px] tracking-[0.12em] flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            style={{ color: "#ef4444" }}
            data-testid="footer-logout"
          >
            <LogOut size={12} />
            LOGOUT
          </button>
        </div>
      </footer>
    </div>
  );
}
